import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  formatPackagePrice,
  getPackageShortDescription,
  getStorageLabel,
} from '../utils/packagePresentation';

function renderLimitValue(pkg, key, t) {
  if (key === 'storageRetentionDays') {
    return getStorageLabel(pkg, t);
  }

  const value = pkg.limits?.[key];
  if (value === null) {
    return t('comparison.value.unlimited', {
      defaultValue: 'Unlimited',
    });
  }

  if (!value) {
    return null;
  }

  return `${value}`;
}

export default function FeatureMatrix() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  const comparisonRows = [
    { key: 'maxRecipients', label: t('comparison.rows.recipients', { defaultValue: 'Recipients' }) },
    { key: 'maxEvents', label: t('comparison.rows.events', { defaultValue: 'Events' }) },
    { key: 'maxMessages', label: t('comparison.rows.messages', { defaultValue: 'Messages' }) },
    { key: 'maxVideos', label: t('comparison.rows.videos', { defaultValue: 'Videos' }) },
    { key: 'maxTemplates', label: t('comparison.rows.templates', { defaultValue: 'Templates' }) },
    { key: 'storageRetentionDays', label: t('comparison.rows.storage', { defaultValue: 'Storage' }) },
  ];

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch('/api/packages');
        if (!response.ok) {
          throw new Error('Failed to fetch packages');
        }

        const data = await response.json();
        setPackages(data.packages || []);
      } catch (error) {
        console.error('Error loading packages:', error);
        toast.error(
          t('comparison.loadError', {
            defaultValue: 'Comparison could not be loaded',
          }),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const sortedPackages = [...packages].sort((a, b) => a.price - b.price);

  return (
    <section id="feature-comparison" className="bg-gradient-to-b from-slate-800 to-slate-900 px-4 py-16">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10 text-center"
        >
          <h2 className="mb-4 text-4xl font-bold text-white md:text-5xl">
            {t('comparison.title', { defaultValue: 'Feature Comparison' })}
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-slate-300 md:text-xl">
            {t('comparison.subtitle', {
              defaultValue: 'The most important differences directly from the package data.',
            })}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="overflow-x-auto rounded-2xl border border-slate-700/50 bg-slate-800/50 backdrop-blur-xl"
        >
          <table className="w-full min-w-[52rem] text-sm md:text-base">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="sticky left-0 bg-slate-900 px-5 py-4 text-left font-semibold text-slate-300">
                  {t('comparison.package', { defaultValue: 'Package' })}
                </th>
                {sortedPackages.map((pkg) => (
                  <th
                    key={pkg.id}
                    className={`px-5 py-4 text-center align-top ${
                      pkg.recommended ? 'bg-purple-900/20' : ''
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl">{pkg.icon}</span>
                      <span className="text-lg font-bold text-white">{pkg.name}</span>
                      <span className="text-sm font-semibold text-purple-300">
                        {formatPackagePrice(
                          pkg.price,
                          i18n.language,
                          t('comparison.value.free', { defaultValue: 'Free' }),
                        )}
                      </span>
                      <span className="max-w-[12rem] text-xs text-slate-400">
                        {getPackageShortDescription(pkg, t)}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, rowIndex) => (
                <tr
                  key={row.key}
                  className={`border-b border-slate-700/30 ${
                    rowIndex % 2 === 0 ? 'bg-slate-900/20' : 'bg-transparent'
                  }`}
                >
                  <td className="sticky left-0 bg-slate-900/70 px-5 py-4 font-medium text-slate-300">
                    {row.label}
                  </td>
                  {sortedPackages.map((pkg) => {
                    const value = renderLimitValue(pkg, row.key, t);

                    return (
                      <td
                        key={`${pkg.id}-${row.key}`}
                        className={`px-5 py-4 text-center text-white ${
                          pkg.recommended ? 'bg-purple-900/10' : ''
                        }`}
                      >
                        {value ? (
                          <span className="font-semibold">{value}</span>
                        ) : (
                          <X size={18} className="mx-auto text-slate-500" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
}
